import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import useOffline from '../../hooks/useOffline';
import useDeliveryNotifications from '../../hooks/useDeliveryNotifications';
import { formatAmount } from '../../utils/format';

import CustomerBottomNav from './CustomerBottomNav';
import CustomerHomeView from './CustomerHomeView';
import CustomerCartView from './CustomerCartView';
import CustomerCheckoutView from './CustomerCheckoutView';
import CustomerOrdersView from './CustomerOrdersView';
import CustomerProfileView from './CustomerProfileView';
const CustomerMobileApp = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  // Shared state
  const [activeTab, setActiveTab] = useState('home');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);
  const [userAddresses, setUserAddresses] = useState([]);
  const [checkoutView, setCheckoutView] = useState(false);

  // Offline hook
  const { cacheData, getCachedData, isOnline } = useOffline();

  // Refresh state for pull-to-refresh
  const [refreshingMenu, setRefreshingMenu] = useState(false);
  const [refreshingOrders, setRefreshingOrders] = useState(false);

  // Product variant selector state
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Sync user addresses
  useEffect(() => {
    if (user?.addresses) {
      setUserAddresses(user.addresses);
    }
  }, [user]);

  // El menú del cliente es POR TENANT del usuario autenticado: sin login no se
  // resuelve tenant_id y por tanto no se cargan productos. Antes se tomaba el
  // primer tenant público (resp.data[0]), lo que mostraba el menú equivocado.
  useEffect(() => {
    if (user?.tenant_id) {
      setTenantId(user.tenant_id);
    } else {
      setTenantId(null);
      setTenant(null);
    }
  }, [user]);

  // Guard: el menú del cliente requiere sesión; sin login se redirige al login.
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [loading, user, navigate]);

  // Load menu when tenantId is available
  const loadMenu = useCallback(async () => {
    try {
      if (!tenantId) { return; }
      const resp = await axios.get(`${API}/mobile/menu/${tenantId}`);
      const cats = resp.data.categories || [];
      const prods = resp.data.products || [];
      // El endpoint ya devuelve el tenant: lo usamos como fuente de verdad
      if (resp.data.tenant) { setTenant(resp.data.tenant); }
      setCategories(cats);
      setProducts(prods);
      
      // Cache data for offline use
      if (isOnline) {
        cacheData('products', prods);
        cacheData('categories', cats);
      }
      
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try { setCart(JSON.parse(savedCart)); } catch { /* ignore corrupt cart data */ }
      }
    } catch (error) {
      console.error('Error loading menu:', error);
      // Try loading from offline cache
      const cachedProds = await getCachedData('products');
      const cachedCats = await getCachedData('categories');
      if (cachedProds.length > 0) { setProducts(cachedProds); }
      if (cachedCats.length > 0) { setCategories(cachedCats); }
    }
  }, [tenantId, isOnline, cacheData, getCachedData]);

  // Load menu when tenantId is available
  useEffect(() => {
    if (tenantId) {
      loadMenu();
    }
  }, [tenantId, loadMenu]);

  // Load loyalty points
  const loadLoyaltyPoints = useCallback(async () => {
    try {
      const savedInfo = localStorage.getItem('customerInfo');
      if (!savedInfo) { setLoyaltyPoints(null); return; }
      const data = JSON.parse(savedInfo);
      if (!data.phone) { setLoyaltyPoints(null); return; }
      const resp = await axios.get(`${API}/loyalty/customers/${data.phone}?tenant_id=${tenantId}`);
      setLoyaltyPoints(resp.data.total_points || 0);
    } catch {
      setLoyaltyPoints(null);
    }
  }, [tenantId]);

  // Load my orders
  const loadMyOrders = useCallback(async (phoneToSearch) => {
    try {
      let phone = phoneToSearch;
      if (!phone) {
        if (user?.phone) {
          phone = user.phone;
        } else {
          const saved = localStorage.getItem('customerInfo');
          if (saved) {
            const d = JSON.parse(saved);
            phone = d.phone;
          }
        }
      }
      if (!phone || !tenantId) { setMyOrders([]); return; }

      const resp = await axios.get(`${API}/mobile/orders/by-phone?phone=${phone}&tenant_id=${tenantId}`);
      const allOrders = (Array.isArray(resp.data) ? resp.data : []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setMyOrders(allOrders);
      loadLoyaltyPoints();
    } catch (error) {
      console.error('Error loading orders:', error);
      setMyOrders([]);
    }
  }, [tenantId, loadLoyaltyPoints, user]);

  // Cargar las órdenes al iniciar sesión para que el sondeo de notificaciones
  // del delivery arranque aunque el cliente no abra la pestaña Órdenes (los
  // cambios de estado se avisan desde cualquier pestaña).
  useEffect(() => {
    if (tenantId && (user?.phone || localStorage.getItem('customerInfo'))) {
      loadMyOrders();
    }
  }, [tenantId, user, loadMyOrders]);

  // Teléfono de respaldo en customerInfo (mismo criterio que loadMyOrders)
  const notifPhone = user?.phone || (() => {
    try { return JSON.parse(localStorage.getItem('customerInfo') || '{}').phone; } catch { return null; }
  })();

  // Notificaciones in-app de los estados del delivery (toasts + badge)
  const { hasUnseenUpdate, clearUnseen } = useDeliveryNotifications({
    orders: myOrders,
    phone: notifPhone,
    tenantId,
    onRefreshOrders: loadMyOrders,
  });

  // Pull-to-refresh handlers
  const handleRefreshMenu = useCallback(async () => {
    setRefreshingMenu(true);
    try {
      await loadMenu();
      toast.success('Menú actualizado');
    } catch {
      toast.error('Error al actualizar el menú');
    } finally {
      setRefreshingMenu(false);
    }
  }, [loadMenu]);

  const handleRefreshOrders = useCallback(async () => {
    setRefreshingOrders(true);
    try {
      await loadMyOrders();
      toast.success('Órdenes actualizadas');
    } catch {
      toast.error('Error al actualizar órdenes');
    } finally {
      setRefreshingOrders(false);
    }
  }, [loadMyOrders]);

  // Cart functions
  const saveCart = (newCart) => {
    setCart(newCart);
    try { localStorage.setItem('cart', JSON.stringify(newCart)); } catch { /* ignore storage errors */ }
  };

  const addToCart = (product) => {
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setSelectedVariant(null);
      setShowVariantSelector(true);
      return;
    }
    const existing = cart.find(item => item.id === product.id && !item.variant_id);
    const item = { id: product.id, name: product.name, price: product.price, category_id: product.category_id, image_url: product.image_url };
    if (existing) {
      saveCart(cart.map(i => i.id === product.id && !i.variant_id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      saveCart([...cart, { ...item, quantity: 1, notes: '' }]);
    }
  };

  const addVariantToCart = () => {
    if (!selectedVariant || !selectedProduct) return;
    const existing = cart.find(i => i.id === selectedProduct.id && i.variant_id === selectedVariant.id);
    const item = {
      id: selectedProduct.id, name: `${selectedProduct.name} ${selectedVariant.name}`,
      price: selectedVariant.price, category_id: selectedProduct.category_id,
      image_url: selectedProduct.image_url, variant_id: selectedVariant.id, variant_name: selectedVariant.name
    };
    if (existing) {
      saveCart(cart.map(i => i.id === selectedProduct.id && i.variant_id === selectedVariant.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      saveCart([...cart, { ...item, quantity: 1, notes: '' }]);
    }
    toast.success(`${item.name} agregado`);
    setShowVariantSelector(false);
    setSelectedProduct(null);
    setSelectedVariant(null);
  };

  const updateQuantity = (productId, change, variantId = null) => {
    saveCart(cart.map(item => {
      const match = variantId
        ? item.id === productId && item.variant_id === variantId
        : item.id === productId && !item.variant_id;
      return match ? { ...item, quantity: Math.max(0, item.quantity + change) } : item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId, variantId = null) => {
    saveCart(cart.filter(item => variantId
      ? !(item.id === productId && item.variant_id === variantId)
      : !(item.id === productId && !item.variant_id)
    ));
  };

  const updateNotes = (productId, notes, variantId = null) => {
    saveCart(cart.map(item => {
      const match = variantId
        ? item.id === productId && item.variant_id === variantId
        : item.id === productId && !item.variant_id;
      return match ? { ...item, notes } : item;
    }));
  };

  // Checkout
  const handleCheckout = async (checkoutData) => {
    const orderItems = cart.map(item => ({
      product_id: item.id,
      variant_id: item.variant_id || null,
      quantity: item.quantity,
      notes: item.notes || ''
    }));
    const orderPayload = {
      order_type: 'delivery',
      customer_name: checkoutData.name,
      customer_phone: checkoutData.phone,
      delivery_address: checkoutData.address,
      items: orderItems,
      discount: 0,
      payment_method: checkoutData.paymentMethod
    };

    try {
      let response;
      if (checkoutData.paymentMethod === 'transfer' && checkoutData.transferProof) {
        const formData = new FormData();
        formData.append('order_data', JSON.stringify(orderPayload));
        formData.append('transfer_proof', checkoutData.transferProof);
        response = await axios.post(`${API}/mobile/orders/create-with-transfer?tenant_id=${tenantId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${API}/mobile/orders/create?tenant_id=${tenantId}`, orderPayload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }

      localStorage.setItem('customerInfo', JSON.stringify({ name: checkoutData.name, phone: checkoutData.phone }));
      saveCart([]);
      setCheckoutView(false);
      toast.success('¡Orden creada exitosamente!');
      navigate(`/delivery-tracking/${response.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear la orden'));
    }
  };

  // Reorder
  const handleReorder = (order) => {
    const itemsToAdd = order.items.map(item => {
      const product = products.find(p => p.id === item.product_id);
      if (!product) return null;
      return { id: product.id, name: product.name, price: item.unit_price || product.price, quantity: item.quantity, notes: '' };
    }).filter(Boolean);

    if (itemsToAdd.length === 0) { toast.error('Productos no disponibles'); return; }
    const newCart = [...cart];
    itemsToAdd.forEach(item => {
      const existing = newCart.find(c => c.id === item.id && !c.variant_id);
      if (existing) existing.quantity += item.quantity;
      else newCart.push(item);
    });
    saveCart(newCart);
    toast.success(`${itemsToAdd.length} producto(s) agregado(s) al carrito`);
    setActiveTab('cart');
  };

  // Handle tab changes
  const handleTabChange = (tab) => {
    if (tab === 'orders') { loadMyOrders(); clearUnseen(); }
    setActiveTab(tab);
    if (tab === 'home') setCheckoutView(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('customerInfo');
    logout();
    navigate('/login');
  };

  // Sin sesión no se renderiza nada (el guard ya redirige al login).
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Cargando...
      </div>
    );
  }
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Safe area padding for notch phones */}
      <div className={`max-w-lg mx-auto px-4 pt-4 relative min-h-screen ${checkoutView ? 'pb-0' : 'pb-20'}`}>
        {/* Tab Content */}
        {checkoutView ? (
          <CustomerCheckoutView
            cart={cart}
            tenant={tenant}
            user={user}
            userAddresses={userAddresses}
            loyaltyPoints={loyaltyPoints}
            onConfirmOrder={handleCheckout}
            onBack={() => setCheckoutView(false)}
          />
        ) : activeTab === 'home' ? (
          <CustomerHomeView
            products={products}
            categories={categories}
            tenant={tenant}
            cart={cart}
            onAddToCart={addToCart}
            onRefresh={handleRefreshMenu}
            isRefreshing={refreshingMenu}
          />
        ) : activeTab === 'cart' ? (
          <CustomerCartView
            cart={cart}
            tenant={tenant}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeFromCart}
            onUpdateNotes={updateNotes}
            onCheckout={() => setCheckoutView(true)}
            onBack={() => setActiveTab('home')}
          />
        ) : activeTab === 'orders' ? (
          <CustomerOrdersView
            myOrders={myOrders}
            onReorder={handleReorder}
            onViewTracking={(id) => navigate(`/delivery-tracking/${id}`)}
            onLoadOrders={(phone) => loadMyOrders(phone)}
            user={user}
            onRefresh={handleRefreshOrders}
            isRefreshing={refreshingOrders}
          />
        ) : activeTab === 'profile' ? (
          <CustomerProfileView
            user={user}
            tenant={tenant}
            loyaltyPoints={loyaltyPoints}
            myOrders={myOrders}
            onLogout={handleLogout}
          />
        ) : null}
      </div>

      {/* Bottom Navigation (oculto en checkout: el contenedor usa pb-0 y el nav
          fijo se solaparía con el botón "Confirmar Pedido") */}
      {!checkoutView && (
        <CustomerBottomNav
          activeTab={activeTab}
          cartCount={cart.length}
          ordersBadge={hasUnseenUpdate}
          onTabChange={handleTabChange}
        />
      )}

      {/* Variant Selector Modal */}
      {showVariantSelector && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowVariantSelector(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md mx-auto p-6 animate-slide-up shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Elige tu opción</h2>
              <button onClick={() => setShowVariantSelector(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="font-semibold text-gray-800 mb-3">{selectedProduct.name}</p>
            <div className="space-y-2 mb-4">
              {selectedProduct.variants.filter(v => v.is_available !== false).map((variant) => {
                const soldOut = variant.tracked === true && Number(variant.stock) <= 0;
                return (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariant(variant)}
                    disabled={soldOut}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all active:scale-[0.98] ${
                      selectedVariant?.id === variant.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-100 bg-gray-50 hover:border-orange-300'
                    } ${soldOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-gray-800">{variant.name}</span>
                        {soldOut && (
                          <p className="text-xs text-red-500">Agotada</p>
                        )}
                        {!soldOut && variant.tracked === true && (
                          <p className="text-xs text-gray-500">Existencia: {Number(variant.stock) || 0}</p>
                        )}
                      </div>
                      <span className="text-lg font-bold text-orange-600">${formatAmount(variant.price)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowVariantSelector(false)} className="flex-1 h-11 border-2 border-gray-200 rounded-xl font-medium text-gray-600 active:scale-[0.98] transition-transform">
                Cancelar
              </button>
              <button
                onClick={addVariantToCart}
                disabled={!selectedVariant}
                className={`flex-1 h-11 rounded-xl font-bold text-white active:scale-[0.98] transition-transform ${
                  selectedVariant ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gray-300'
                }`}
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerMobileApp;
